/**
 * Village drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const villageDrawingsPack: ToilePack = {
    slug: 'village-drawings',
    category: 'coloring',
    name: 'Village drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hut', label: 'Hut and river', subject: 'two thatched huts beside a river with a coconut tree, a boat and hills behind'},
        {key: 'farm', label: 'Farm field', subject: 'a farmer ploughing with two bullocks, a field of rows and a sun over the hills'},
        {key: 'well', label: 'Village well', subject: 'women drawing water at a stone well under a banyan tree, a hut behind'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['at sunset', 'a pond', 'palm trees'],
    directionHint: 'try: at sunset',
    seo: {
        title: 'Village Drawings: Hut by River, Farm Field, Village Well',
        metaDescription: 'Nine village drawings: a hut by a river, a farmer ploughing with bullocks and women at a well, in pencil, colour and outline.',
        h1: 'Village drawings: hut by a river, farm field, village well',
        tagline: 'Three village scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Village drawings for a competition, a scenery project or a young child. The set shows two thatched huts by a river with a coconut tree, a farmer ploughing a field with two bullocks, and women drawing water at a stone well. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as at sunset or a pond.',
        useCases: [
            'a village scenery drawing for a competition',
            'a village life chart for a class',
            'an easy colouring page',
            'a classroom wall display',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Do the pictures show a real village?', a: 'They are drawn scenes. Each one shows typical village things, such as huts, a field and a well, and not a particular place.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field, such as palm trees. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
